import type { EventId, FigureId, SettlementId, TilePos } from "../world/types";

/*
 * The interact verb: which thing in the world is the player addressing.
 *
 * Proximity and facing, against a list the client keeps of its own scene
 * objects — **not** a raycast. `player/raycast.ts` traverses the voxel grid and
 * can only ever name a tile (that is the point of it: the scene's geometry is
 * merged slabs, so a mesh intersection would name a triangle belonging to a quad
 * spanning a dozen tiles). An NPC is not a tile and never will be, so asking the
 * grid about one is asking the wrong question. This file answers the other one.
 *
 * The selection is deliberately forgiving, and the two halves of it are
 * forgiving in different ways:
 *
 * - **Range is measured in three dimensions** from the player's feet, so
 *   somebody standing on the terrace above is out of reach at the distance they
 *   look out of reach at, without a special case for height.
 * - **The cone is measured in two.** Pitch is the crosshair's business — the dig
 *   ray owns "what am I pointing at" and owns it precisely. Talking to somebody
 *   is about which way you are *turned*, and a player who looks at their feet
 *   mid-sentence has not stopped addressing the person in front of them.
 *
 * Nothing here decides what an interaction *does*. It answers "who", the caller
 * fires the verb, and what the verb says comes out of the simulation
 * (`world/types.ts`, `WitnessSource.speak`).
 *
 * The aiming numbers' history — and the argument for the one command that cites a
 * person — is in `docs/decisions/player.md`.
 */

/** What the player would be addressing, and the handle the caller needs to do it. */
export type InteractSubject =
  | {
      readonly kind: "npc";
      readonly figure: FigureId;

      /**
       * The tile the simulation has this body on, or `null` for a body the
       * verb has nothing to do with beyond talking.
       *
       * A *tile* rather than the drawn point, because the carry verbs cite
       * ground the way every other Phase 7 verb does (see the block comment
       * below): `take` and `drop` name a tile and the simulation picks what is
       * on it (`sim_core::hands`).
       */
      readonly at: TilePos | null;

      /**
       * What the verb does to this body: talk to it, pick it up, or put it
       * down (wave 2A).
       *
       * **The simulation decides which** — a body is liftable when its life
       * stage's size row says so ({@link WitnessSource.lifeStagePortable}) and
       * `set down` is offered when the player is the one carrying it
       * ({@link PresentFigure.heldBy}) — so this column is an answer being
       * carried rather than a judgement being made here. A client that decided
       * for itself would be the second copy of a table the
       * `no press the sim refuses silently` rule exists to prevent.
       */
      readonly carry: "none" | "take" | "drop";

      /**
       * What the *company* verb does to this body: take them along, send them
       * on their way, or nothing (wave 2F).
       *
       * **The simulation decides which**, exactly as `carry` above it: a figure
       * offers to follow a hero whose deeds have reached them
       * ({@link WitnessSource.offersToFollow}) and a companion is somebody the
       * world says already follows the player ({@link WitnessSource.leaderOf}).
       * A client that decided for itself would be keeping a copy of a rule that
       * reads two dials, a life stage, an engagement and a town's rumour store.
       */
      readonly follow: "none" | "beckon" | "dismiss";
    }

  /** The settlement's market board — a feature id, like the rest of the ground. */
  | { readonly kind: "market"; readonly feature: number }

  /**
   * Anything the history log left standing: a grave, a burned farmstead, a
   * battlefield marker. One kind rather than three, because the verb is the
   * same — the player reads what is written there, and what is written there is
   * a projection over the records in {@link Interactable.refs}.
   */
  | { readonly kind: "grave"; readonly feature: number }

  /**
   * A group of refugees on the road, who carry the news they are fleeing.
   *
   * The only body on a road that is still a subject: the trade caravan's cart
   * was retired with the client-side walk that placed it, and a trade pulse is
   * read on the board and by the director, never met. When columns walk the
   * real road the figure in one is an `npc`, not a new kind.
   */
  | { readonly kind: "refugees"; readonly group: number }

  /**
   * A book somebody left standing in a town, by the simulation's own handle for
   * it (`world/types.ts`'s `ChronicleView`).
   *
   * A separate kind from `grave` although both are read, because what is read
   * comes off a different query: a stone's text is `inscription(feature)` and a
   * book's is `chronicleLines(chronicle)`, which is a *page per record* rather
   * than one carving. The handle is the chronicle's rather than a feature's for
   * the same reason — a book is not aftermath, it is a thing somebody wrote
   * (Phase 5 decision 5).
   */
  | { readonly kind: "chronicle"; readonly chronicle: number }

  /**
   * The Book of Eras: the world's own history, standing on the same shelf
   * (Phase 6 decision O6).
   *
   * A separate kind from `chronicle` although it is a book on a shelf, and the
   * handle is the whole of the reason: a chronicle is a thing in the world with
   * an id, and this is a **projection** — derived on every read, stored nowhere,
   * belonging to no author (`witness/saga.ts`). There is nothing to carry, so
   * the subject carries nothing.
   *
   * The verb is the same and so is the panel, which is the exit criterion: the
   * same text the session opened on, readable in-world.
   */
  | { readonly kind: "eras" }

  /**
   * The wreckage of the last army through a town, by the town's own handle.
   *
   * Read rather than looked at, and that is what makes it a subject at all: a
   * ruin cites the battle that caused it (`world/types.ts`'s `RuinView.sack`),
   * so walking up to the char and pressing F is how a player finds out what
   * happened here — the same bargain a grave makes, over a different projection
   * (Phase 5 decision 11). The settlement is the handle because the ruin is the
   * *town's* state and the heaps drawn around its centre are all one ruin;
   * reading any of them is reading it.
   */
  | { readonly kind: "ruin"; readonly settlement: SettlementId }

  /*
   * Phase 7's three, and every one of them is a *tile* rather than a handle.
   *
   * That is the wave's own rule rather than a convenience: a command cites the
   * work and never the board row (plan O5), and a pickup cites the ground and
   * never an item (`sim_core::hands`). A client that carried a handle here would
   * be carrying something it read a frame ago, and a pile that moved under it
   * would put the wrong thing in somebody's hands.
   */

  /**
   * Something lying on the ground: the spoil of a dig, a felled log, whatever
   * somebody put down.
   *
   * The verb takes it, and *which* it takes is the simulation's rule — the first
   * item in handle order. What the prompt says it is, is the label of that first
   * item ({@link WitnessSource.itemLabel}), which is the same thing the command
   * will pick.
   */
  | { readonly kind: "item"; readonly at: TilePos; readonly label: string }

  /**
   * A workshop's bench: a forge, a carpenter's, a kitchen, a library's desk.
   *
   * The tile is the lot's own work tile, which is the tile
   * {@link WorldSource.craft} is sent to and the tile the marketplace posts its
   * own orders at — the offer and the command are one row
   * ({@link WitnessSource.lots}).
   *
   * **Whether a building has work in it is the simulation's answer** and not a
   * list kept here: a lot's `trade` column is `null` for a home and a
   * storehouse, and this subject is only made where it is not.
   */
  | {
      readonly kind: "workshop";
      readonly at: TilePos;

      /** What the building is called, in the simulation's own word: *smithy*. */
      readonly building: string;
    }

  /**
   * A library's shelf, whatever is or is not on it.
   *
   * **The empty-shelf invitation** (Phase 7 plan, decision 14): a shelf with
   * books opens them, and a shelf without says that nobody in this town has
   * written anything down. Those are two answers to one press, which is why the
   * subject is the *shelf* rather than a book — a town with a bare shelf still
   * has something to say, and a subject keyed on a book would have nothing to
   * be.
   */
  | { readonly kind: "shelf"; readonly at: TilePos };

/** One thing the player could address, as the client's entity list holds it. */
export interface Interactable {
  /** Stable for as long as the thing exists. The prop registry keys off the same id. */
  readonly id: string;

  /** What the player would call it: "Halvard Reedmere", "the market board", "a grave". */
  readonly label: string;

  /** Where it stands, in world tiles. Feet, not eyes. */
  readonly x: number;
  readonly y: number;
  readonly z: number;

  readonly subject: InteractSubject;

  /**
   * The records behind it, for a channel that speaks without being asked.
   *
   * A grave and a refugee group know what they are about before the player
   * arrives; an NPC does not, because which rumour they reach for is the
   * simulation's choice at the moment of asking. Empty for those.
   */
  readonly refs: readonly EventId[];
}

/**
 * How far the player can reach to interact, in tiles.
 *
 * PROVISIONAL. Shorter than `DIG_RANGE_TILES`'s six on purpose: a pick has a
 * handle and a conversation has a personal space, and three tiles is close
 * enough that the player has visibly walked up to somebody. It coincides with
 * DF's `stationdist` of 3 (`pathfinding.md` §2.3) by accident rather than by
 * derivation, and the two numbers are not linked.
 */
export const INTERACT_RANGE_TILES = 3;

/**
 * Half the angle of the facing cone, in degrees. PROVISIONAL.
 *
 * Forty-five degrees each way is a ninety-degree wedge — wide enough that
 * walking up to somebody and pressing the key works without aiming, narrow
 * enough that with two people either side of a market board the player can pick
 * which one they meant by turning.
 */
export const INTERACT_CONE_DEGREES = 45;

/** The same cone as a dot-product threshold, which is what the test actually is. */
export const INTERACT_CONE_COS = Math.cos((INTERACT_CONE_DEGREES * Math.PI) / 180);

/*
 * Aiming a blow (Phase 7, wave 2D-a, plan W8).
 *
 * **The pick is a call, not a third selector.** {@link selectInteractable} takes
 * its range and its cone as arguments precisely so that a verb with a different
 * reach is a different *call site* over the same algorithm — the interact doc
 * predicted a third one and this is it, and there is no new nearest-in-a-cone
 * code anywhere in this client.
 *
 * # And the one place the wave's own rule is broken on purpose
 *
 * Every Phase 7 verb above cites a **tile**, because a command cites the work
 * and never the board row (plan O5) and a pickup cites the ground and never an
 * item — a handle read a frame ago is a handle that may have moved. A strike
 * cites a **figure**, and the exception is argued rather than assumed:
 *
 * - **a figure id is stable where a board row was not.** Nothing ever leaves the
 *   simulation's figure arena, so a display number belongs to one person for the
 *   life of the world — which is the same property `Command::Born`'s bound door
 *   already relies on, and it is the property a board row does not have;
 * - **a tile could not say who.** Two people on one doorstep are two different
 *   swings, and a command that named the ground would pick between them by a
 *   rule nobody could aim with.
 *
 * The simulation carries the same paragraph beside the command itself
 * (`sim_core::Command::Strike`), so the exception is written down on both sides
 * of the wall rather than inferred from one.
 */

/**
 * How far the player can reach to strike, in tiles.
 *
 * **Deliberately wider than the simulation's own reach**, and the gap is the
 * whole of what this number is for: the sim measures Chebyshev between two
 * *tiles* ({@link withinStrikeReach}) and this measures Euclidean between the
 * player's feet and a drawn body, so a pick that used the sim's number would
 * miss bodies the sim would happily let the player hit. What keeps it honest is
 * that the *offer* is narrowed by the sim's own predicate afterwards
 * (`witness/witness.ts`'s `foeInReach`), so nothing is ever prompted that the
 * command would refuse.
 *
 * PROVISIONAL, and it is a taste number about aiming rather than a rule.
 */
export const STRIKE_RANGE_TILES = 4;

/**
 * Half the angle of the striking cone, in degrees. PROVISIONAL.
 *
 * **Narrower than {@link INTERACT_CONE_DEGREES}'s forty-five**, and for the
 * reason the two verbs differ: talking to somebody is about which way you are
 * turned, and swinging at somebody is about what is in front of you. Thirty
 * degrees each way is a sixty-degree wedge — wide enough to hit what the
 * crosshair is over without aiming precisely, narrow enough that with two people
 * either side of a doorway the player chooses by turning.
 */
export const STRIKE_CONE_DEGREES = 30;

/** The same cone as a dot-product threshold ({@link INTERACT_CONE_COS}'s twin). */
export const STRIKE_CONE_COS = Math.cos((STRIKE_CONE_DEGREES * Math.PI) / 180);

/**
 * How far the player can pick a target to shoot at, in tiles.
 *
 * {@link STRIKE_RANGE_TILES}' twin over the bow's own distance, and wider than
 * the simulation's for its exact reason: this measures feet to a drawn body and
 * the sim measures tile to tile, so a pick using the sim's number would miss
 * bodies the sim would happily let the arrow reach. The offer is narrowed by the
 * sim's own predicate afterwards (`world/types.ts`'s `withinBowRange`), so
 * nothing is ever prompted that the command would refuse.
 *
 * PROVISIONAL, and derived rather than picked: **two tiles of slack**, which is
 * the slack the strike pick already carries over its own reach.
 */
export const LOOSE_RANGE_TILES = 14;

/**
 * Half the angle of the shooting cone, in degrees. PROVISIONAL.
 *
 * **Narrower than {@link STRIKE_CONE_DEGREES}' thirty**, and the two verbs'
 * difference is why: a swing is at what is in front of you, and a shot is at
 * what you are *aiming* at. Fifteen degrees each way is a thirty-degree wedge —
 * still forgiving of a crosshair a few pixels off a body twelve tiles away, and
 * narrow enough that a crowd at the end of a street is picked between by
 * turning.
 */
export const LOOSE_CONE_DEGREES = 15;

/** The same cone as a dot-product threshold ({@link STRIKE_CONE_COS}'s twin). */
export const LOOSE_CONE_COS = Math.cos((LOOSE_CONE_DEGREES * Math.PI) / 180);

/**
 * Returns the nearest interactable in range and in front of the player, or
 * `null`.
 *
 * Nearest rather than best-aligned: with the cone already filtering out anything
 * the player is not turned towards, "the closest of what I am facing" is the
 * answer that matches what a player would say they meant.
 *
 * **Ties go to the earlier entry, and since chunk B the order of the list is a
 * rule rather than an accident.** It used to be described here as arbitrary but
 * stable — enough to stop a prompt flickering between two things on one spot,
 * and no help at all in deciding which of them a player meant. A town made of
 * tiles produces that collision every time somebody walks into a workshop: the
 * bench and everything lying on it are one column at one probed height, so they
 * are at exactly one distance and the tie is the whole decision. The rule the
 * caller now states, and states where the list is built
 * (`witness/witness.ts`'s `render`): **a thing standing on a tile is addressed
 * before the tile's own standing verb** — a heap is taken once and a bench is
 * offered for ever, so the bench is the one that can afford to yield.
 *
 * What keeps that from being the old defect back again is that no subject on
 * this list offers a press the simulation refuses in silence: a pile with
 * nothing portable in it produces no subject at all, so a smithy's own table no
 * longer takes the bench's tile with a promise it cannot keep.
 */
export function selectInteractable(
  from: { readonly x: number; readonly y: number; readonly z: number },
  yaw: number,
  candidates: readonly Interactable[],
  range: number = INTERACT_RANGE_TILES,
  coneCos: number = INTERACT_CONE_COS,
): Interactable | null {
  // Yaw 0 looks along +y and positive yaw turns left — `player/controller.ts`'s
  // convention, transcribed here rather than shared because the controller's
  // version is a movement basis and this one is a single direction.
  const forwardX = -Math.sin(yaw);
  const forwardY = Math.cos(yaw);

  let best: Interactable | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const dx = candidate.x - from.x;
    const dy = candidate.y - from.y;
    const dz = candidate.z - from.z;
    const distance = Math.hypot(dx, dy, dz);

    if (distance > range || distance >= bestDistance) {
      continue;
    }

    const horizontal = Math.hypot(dx, dy);

    // Standing inside it: there is no direction to test, and refusing the
    // interaction because the player is too close would be the one failure mode
    // a proximity verb must not have.
    if (horizontal > 0 && (forwardX * dx + forwardY * dy) / horizontal < coneCos) {
      continue;
    }

    best = candidate;
    bestDistance = distance;
  }

  return best;
}

/**
 * The HUD's one-line prompt for a target: "F — talk to Halvard Reedmere".
 *
 * Here rather than in `hud/panels.ts` because the verb belongs to the subject:
 * the HUD's job is to draw a string, and deciding that a grave is read and a
 * person is talked to is this module's.
 *
 * `key` is what the player presses, and `null` is a session where there is
 * nothing to press — a touchscreen, where the verb is on a button under the
 * thumb and the line reads *"talk to Halvard Reedmere"* (`input/touch.ts`,
 * `docs/decisions/input.md`). A parameter rather than a second function, because
 * the *sentence* is the same sentence: what changes is whether there is a
 * keyboard to name in it.
 *
 * # Legibility gap 5, and what was found when it was opened (chunk U-1)
 *
 * The gap the owner filed is *"prompts that say what can be done here"*
 * (`phase-7-plan.md`, the 2026-08-30 list), and the graduation plan's reading of
 * it was that this function names a key and leaves the subject unsaid. **It does
 * not, and it has not since Phase 7 wave 1.** Every one of the eleven subjects
 * {@link InteractSubject} can be is named in the line, and the name is composed
 * where the thing is collected rather than here:
 *
 * | subject | the line | where the noun comes from |
 * |---|---|---|
 * | `npc` | *F — talk to Halvard Reedmere* | the simulation's own spelling of a name |
 * | `market` | *F — read the market board* | `labelForFeature`, client chrome by ruling |
 * | `grave` | *F — read a grave* | the same |
 * | `refugees` | *F — ask refugees from Stonehearth (12)* | the origin town and the sim's count |
 * | `chronicle` | *F — read a book by Ulfsten* | the sim's author |
 * | `eras` | *F — read the book of eras* | the plan's own name for it |
 * | `ruin` | *F — read …* | the burned town's name |
 * | `item` | *F — pick up granite boulder* | `itemLabel`, the sim's |
 * | `workshop` | *F — work at smithy* | the lot's trade, the sim's word |
 * | `shelf` | *F — read a shelf* | client chrome |
 *
 * So what this chunk did to the gap is **make the shape legible rather than the
 * words longer**: the key is cut off the front of this sentence and drawn as a
 * chip, so a prompt reads as *press this → this happens* before it is read at
 * all (`hud/panels/prompt.ts`). The em-dash below is what that cut is made at,
 * and it is why the separator is stated here in one place rather than spelled at
 * two call sites.
 *
 * **Two remainders are filed rather than closed, and both need a hand outside
 * this module's fence:**
 *
 * - **a subject that affords two things shows one.** {@link verbFor}'s `npc`
 *   case walks a precedence — put down, pick up, take along, send away, talk to
 *   — and everything below the winner is invisible, so a companion is somebody
 *   you cannot ask for news and a baby in the street is somebody you cannot talk
 *   to. The subject already carries the columns that would say so (`carry`,
 *   `follow`); what it has no room for is a second key, and the plan puts a
 *   person's second door in the conversation area.
 * - **a shelf does not say what is on it.** The `shelf` arm carries a tile and
 *   nothing else, so the prompt cannot tell a library from an empty room until
 *   the player has pressed `F` — and `witness/witness.ts` knows the answer at
 *   the moment it builds the subject (it has the town's books in hand). One
 *   field, `books: number`, filled where `pileSubject` makes the arm, and this
 *   function can say *read a shelf · two books* / *· nothing on it*.
 */
export function promptFor(target: Interactable, key: string | null = "F"): string {
  const doing = `${verbFor(target)} ${target.label}`;

  return key === null ? doing : `${key}${PROMPT_DASH}${doing}`;
}

/**
 * What every prompt line in this client puts between the key and the verb.
 *
 * An em-dash with a space each side, and it is exported because it is a
 * **contract rather than punctuation**: `hud/panels/prompt.ts` cuts a line here
 * to draw the key as a `kbd` chip, so a producer that spelled it differently
 * would silently lose its chip. Four other places compose one of these lines
 * (`hud/crossing.ts`'s three labels, `witness/arms.ts`'s `loosePrompt`, and the
 * swing prompt in `witness/witness.ts`) and `dev/selfcheck/telling.ts` holds all
 * of them to this string.
 */
export const PROMPT_DASH = " — ";

/**
 * What the player does to a subject.
 *
 * Exported since the touch pass, for one caller: the on-screen interact button
 * says what it would do, and it has to say the *same word* the prompt beside it
 * is saying or two surfaces disagree about one thing. `main.ts` reads it across,
 * so `input/` keeps no dependency on this layer.
 */
export function verbFor(target: Interactable): string {
  switch (target.subject.kind) {
    case "npc":
      // Three verbs on one subject, and the *simulation* picked which
      // ({@link InteractSubject}): a grown neighbour is talked to, a baby lying
      // in the street is picked up, and a baby in your arms is set down.
      switch (target.subject.carry) {
        case "take":
          return "pick up";
        case "drop":
          return "set down";
        case "none":
          break;
      }

      // **And the company verbs, under the carry verbs and over the talk**
      // (wave 2F). The order is the subject's own precedence: a body in your
      // arms is put down before anything else is said to it, and somebody
      // offering to come with you is answered before they are asked for news.
      //
      // The cost is stated rather than hidden: while an offer stands, and for
      // as long as somebody follows the player, `F` is not *talk to* — one
      // subject carries one verb here, which is the rule the carry column set
      // when a baby in the street stopped being somebody to talk to. The
      // conversation area is where a person with two things to say to them gets
      // a second door.
      switch (target.subject.follow) {
        case "beckon":
          return "take along";
        case "dismiss":
          return "send away";
        case "none":
          return "talk to";
      }
    case "market":
      return "read";
    case "grave":
      return "read";
    case "refugees":
      return "ask";
    case "chronicle":
      return "read";
    case "eras":
      return "read";
    case "ruin":
      return "read";
    case "item":
      return "pick up";
    case "workshop":
      return "work at";
    case "shelf":
      return "read";
  }
}
